<template>
    <Teleport to="body">
        <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
            <!-- Backdrop -->
            <div class="absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity" @click="closeModal"></div>

            <!-- Modal Content -->
            <div
                class="relative w-full max-w-4xl max-h-[90vh] bg-gray-900/95 backdrop-blur-2xl rounded-2xl shadow-2xl border border-white/10 overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">

                <!-- Header -->
                <div
                    class="flex items-center justify-between p-4 md:p-6 border-b border-white/10 bg-black/20 backdrop-blur-md sticky top-0 z-10">
                    <h2 class="text-xl md:text-2xl font-bold text-white">
                        <span class="text-red-600 mr-2">{{ resolveSessionTitle(sessionName) }}</span>
                        <span class="text-gray-400 font-medium text-lg">{{ race?.raceName }}</span>
                    </h2>
                    <button @click="closeModal"
                        class="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10">
                        <i class="fas fa-times"></i>
                    </button>
                </div>

                <!-- Content Area -->
                <div class="overflow-y-auto p-4 md:p-6 custom-scrollbar flex-1 relative">

                    <!-- Loading State -->
                    <div v-if="isLoading" class="flex flex-col items-center justify-center py-20">
                        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
                        <p class="mt-4 text-gray-500 animate-pulse">Caricamento risultati...</p>
                    </div>

                    <!-- Results Table -->
                    <div v-else-if="results && results.length > 0" class="overflow-x-auto">
                        <table class="w-full text-sm text-left text-gray-300">
                            <thead class="text-xs text-gray-400 uppercase bg-white/5 border-b border-white/10">
                                <tr>
                                    <th scope="col" class="px-4 py-3 text-center w-16">Pos</th>
                                    <th scope="col" class="px-4 py-3">Pilota</th>
                                    <th scope="col" class="px-4 py-3 hidden md:table-cell">Scuderia</th>
                                    <th scope="col" class="px-4 py-3 text-right">Tempo / Distacco</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(result, index) in results" :key="index"
                                    class="border-b border-white/5 hover:bg-white/5 transition-colors">
                                    <!-- Pos -->
                                    <td class="px-4 py-4 text-center font-bold text-white">
                                        {{ result.position || (index + 1) }}
                                    </td>
                                    <!-- Pilota -->
                                    <td class="px-4 py-4 font-medium text-white">
                                        <div class="flex items-center">
                                            <div :style="{ backgroundColor: result.team_colour ? '#' + result.team_colour : getConstructorColor(result) }"
                                                class="w-1 h-5 rounded mr-3 transition-colors duration-300 shadow-[0_0_8px_rgba(0,0,0,0.5)]">
                                            </div>
                                            {{ getDriverName(result) }}
                                        </div>
                                    </td>
                                    <!-- Scuderia -->
                                    <td class="px-4 py-4 hidden md:table-cell">
                                        {{ getConstructorName(result) }}
                                    </td>
                                    <!-- Tempo -->
                                    <td class="px-4 py-4 text-right font-mono text-sm">
                                        {{ getResultTime(result, index) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Empty / Future State -->
                    <div v-else class="flex flex-col items-center justify-center py-20 text-center">
                        <div
                            class="w-20 h-20 bg-white/5 border border-white/10 rounded-full flex items-center justify-center mb-6 shadow-inner">
                            <i class="fas fa-flag-checkered text-3xl text-gray-500"></i>
                        </div>
                        <h3 class="text-xl font-bold text-white mb-2">Sessione in attesa</h3>
                        <p class="text-gray-400 max-w-md">I risultati di questa sessione non sono
                            ancora disponibili. Riprova dopo la conclusione dell'evento.</p>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script>
import { ref, watch, onMounted } from 'vue';
import { f1Service } from '../services/f1Service';
import { openF1Service } from '../services/openF1Service';
import { getTeamColor } from '../utils/teamColors';

export default {
    name: 'SessionResultsModal',
    props: {
        isOpen: {
            type: Boolean,
            required: true
        },
        sessionName: {
            type: String,
            default: ''
        },
        race: {
            type: Object,
            default: null
        }
    },
    emits: ['close'],
    setup(props, { emit }) {
        const isLoading = ref(false);
        const results = ref([]);
        const firstPlaceTime = ref(null);

        const closeModal = () => {
            emit('close');
        };

        const resolveSessionTitle = (name) => {
            const map = {
                FirstPractice: 'Prove Libere 1',
                SecondPractice: 'Prove Libere 2',
                ThirdPractice: 'Prove Libere 3',
                SprintQualifying: 'Qualifiche Sprint',
                Qualifying: 'Qualifiche',
                Sprint: 'Sprint',
                Race: 'Gara'
            };
            return map[name] || name;
        };

        const loadResults = async () => {
            if (!props.isOpen || !props.race || !props.sessionName) return;

            isLoading.value = true;
            results.value = [];
            firstPlaceTime.value = null;

            try {
                const season = props.race.season;
                const round = props.race.round;
                const name = props.sessionName;

                let data = null;

                if (name === 'Qualifying') {
                    data = await f1Service.getQualifyingResults(season, round);
                } else if (name === 'Sprint') {
                    data = await f1Service.getSprintResults(season, round);
                } else if (name === 'Race') {
                    data = await f1Service.getRaceResults(season, round);
                } else if (name === 'SprintQualifying') {
                    const sessionDate = props.race.SprintQualifying?.date;
                    if (sessionDate) {
                        data = await openF1Service.getSprintQualiResults(season, sessionDate);
                    }
                } else if (name.includes('Practice')) {
                    const openF1Name = name === 'FirstPractice' ? 'Practice 1'
                        : name === 'SecondPractice' ? 'Practice 2'
                            : 'Practice 3';
                    const sessionDate = props.race[name]?.date;
                    if (sessionDate) {
                        data = await openF1Service.getPracticeResults(season, sessionDate, openF1Name);
                    }
                }

                if (data && data.length > 0) {
                    results.value = data;
                }
            } catch (error) {
                console.error('Error fetching session results:', error);
            } finally {
                isLoading.value = false;
            }
        };

        const formatLapTime = (seconds) => {
            if (!seconds) return '';
            const mins = Math.floor(seconds / 60);
            const secs = (seconds % 60).toFixed(3);
            return mins > 0 ? `${mins}:${secs.padStart(6, '0')}` : secs;
        };

        const getDriverName = (row) => {
            // Jolpi
            if (row.Driver) {
                return `${row.Driver.givenName} ${row.Driver.familyName}`;
            }
            // OpenF1
            if (row.full_name) {
                return row.full_name;
            }
            return 'Pilota Sconosciuto';
        };

        const getConstructorName = (row) => {
            // Jolpi
            if (row.Constructor) {
                return row.Constructor.name;
            }
            // OpenF1
            if (row.team_name) {
                return row.team_name;
            }
            return '-';
        };

        const getConstructorColor = (row) => {
            if (row.Constructor) {
                return getTeamColor(row.Constructor.constructorId);
            }
            return '#666';
        };

        const getResultTime = (row, index) => {
            // Quali
            if (row.Q3 || row.Q2 || row.Q1) {
                return row.Q3 || row.Q2 || row.Q1;
            }

            // Sprint/Race
            if (row.status === 'Finished' || (row.status && row.status.includes('+'))) {
                return row.Time?.time || row.status;
            } else if (row.status) {
                return row.status; // Retirements, accidents, etc.
            }

            // OpenF1 Practice
            if (row.bestLap) {
                if (index === 0) {
                    firstPlaceTime.value = row.bestLap;
                    return formatLapTime(row.bestLap);
                }
                if (firstPlaceTime.value) {
                    const diff = row.bestLap - firstPlaceTime.value;
                    return `+${diff.toFixed(3)}`;
                }
                return formatLapTime(row.bestLap);
            }

            return '-';
        };

        watch(() => props.isOpen, (newVal) => {
            if (newVal) {
                loadResults();
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
        });

        onMounted(() => {
            if (props.isOpen) {
                loadResults();
            }
        });

        return {
            isLoading,
            results,
            closeModal,
            resolveSessionTitle,
            getDriverName,
            getConstructorName,
            getConstructorColor,
            getResultTime
        };
    }
};
</script>
